import React, { useState, useEffect, useRef } from 'react';
import Dashboard from './components/Dashboard';
import ProjectsTable from './components/ProjectsTable';
import GanttChart from './components/GanttChart';
import SalaryBurn from './components/SalaryBurn';
import { PROJECTS, DEVELOPERS } from './data/projects';

const CORRECT_PASSWORD = '2321';
const AUTH_KEY = 'pipeline-auth-v1';

const NAV = [
  { id: 'dashboard', label: 'Dashboard',      icon: '📊', desc: 'Overview & KPIs' },
  { id: 'projects',  label: 'All Projects',   icon: '📋', desc: 'Full pipeline list' },
  { id: 'gantt',     label: 'Gantt Timeline', icon: '📅', desc: 'Jan 2024 → Dec 2026' },
  { id: 'salary',    label: 'Salary Burn',    icon: '💰', desc: 'Cost breakdown' },
];

// ── Password gate ─────────────────────────────────────────────────────────────

function PasswordGate({ onUnlock }) {
  const [input, setInput] = useState('');
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);

  function tryUnlock(e) {
    e.preventDefault();
    if (input === CORRECT_PASSWORD) {
      localStorage.setItem(AUTH_KEY, '1');
      onUnlock();
    } else {
      setError(true);
      setShake(true);
      setInput('');
      setTimeout(() => setShake(false), 500);
    }
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: '#060d1a',
      fontFamily: "'DM Mono', 'Fira Code', 'Consolas', monospace",
    }}>
      <div style={{
        background: '#080f20', border: '1px solid #1e293b',
        borderRadius: 16, padding: '48px 40px', width: 320,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24,
        animation: shake ? 'shake 0.4s ease' : 'none',
      }}>
        <style>{`
          @keyframes shake {
            0%,100% { transform: translateX(0); }
            20%      { transform: translateX(-10px); }
            40%      { transform: translateX(10px); }
            60%      { transform: translateX(-8px); }
            80%      { transform: translateX(8px); }
          }
        `}</style>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
          <div style={{ fontSize: 11, fontWeight: 900, letterSpacing: 3, color: '#3b82f6', marginBottom: 4 }}>CTO COMMAND</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: '#f1f5f9' }}>Pipeline</div>
          <div style={{ fontSize: 10, color: '#334155', marginTop: 4, letterSpacing: 1 }}>RESTRICTED ACCESS</div>
        </div>
        <form onSubmit={tryUnlock} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input
            type="password"
            value={input}
            onChange={e => { setInput(e.target.value); setError(false); }}
            placeholder="Enter password"
            autoFocus
            style={{
              width: '100%', padding: '12px 16px', borderRadius: 8,
              background: '#0a0f1e',
              border: `1px solid ${error ? '#ef4444' : '#1e293b'}`,
              color: '#e2e8f0', fontSize: 14, outline: 'none',
              fontFamily: 'inherit', textAlign: 'center', letterSpacing: 6,
              boxSizing: 'border-box',
            }}
          />
          {error && <div style={{ fontSize: 11, color: '#ef4444', textAlign: 'center' }}>Incorrect password</div>}
          <button type="submit" style={{
            padding: '11px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: '#1e3a6e', color: '#60a5fa', fontWeight: 700, fontSize: 13,
            fontFamily: 'inherit', letterSpacing: 1,
          }}>UNLOCK</button>
        </form>
      </div>
    </div>
  );
}

// ── Server API ────────────────────────────────────────────────────────────────

async function apiLoad() {
  try {
    const res = await fetch('/api/data');
    return res.ok ? res.json() : {};
  } catch { return {}; }
}

function apiSave(payload) {
  fetch('/api/data', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(console.error);
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  const [unlocked,  setUnlocked]  = useState(() => localStorage.getItem(AUTH_KEY) === '1');
  const [tab,       setTab]       = useState('dashboard');
  const [loading,   setLoading]   = useState(true);

  const [developers,      setDevelopers]      = useState(DEVELOPERS);
  const [projectOverrides, setProjectOverrides] = useState({});
  const [customProjects,  setCustomProjects]  = useState([]);

  // Refs so update callbacks always see latest state without stale closures
  const devsRef     = useRef(DEVELOPERS);
  const overridesRef = useRef({});
  const customRef   = useRef([]);
  const initialized = useRef(false);

  useEffect(() => { devsRef.current     = developers;       }, [developers]);
  useEffect(() => { overridesRef.current = projectOverrides; }, [projectOverrides]);
  useEffect(() => { customRef.current   = customProjects;   }, [customProjects]);

  // Load from server on mount
  useEffect(() => {
    apiLoad().then(data => {
      if (data.developers?.length)    setDevelopers(data.developers);
      if (data.projectOverrides)      setProjectOverrides(data.projectOverrides);
      if (data.customProjects?.length) setCustomProjects(data.customProjects);
      initialized.current = true;
    }).catch(() => { initialized.current = true; })
      .finally(() => setLoading(false));
  }, []);

  function save(devs, overrides, custom) {
    if (!initialized.current) return;
    apiSave({ developers: devs, projectOverrides: overrides, customProjects: custom });
  }

  function updateDeveloper(devId, changes) {
    setDevelopers(prev => {
      const next = prev.map(d => d.id === devId ? { ...d, ...changes } : d);
      save(next, overridesRef.current, customRef.current);
      return next;
    });
  }

  function addDeveloper(devData) {
    setDevelopers(prev => {
      const maxNum = prev.reduce((m, d) => Math.max(m, parseInt(d.id.replace('d', ''), 10) || 0), 0);
      const next = [...prev, { id: `d${maxNum + 1}`, status: 'Active', endDate: null, ...devData }];
      save(next, overridesRef.current, customRef.current);
      return next;
    });
  }

  function updateProject(projectId, changes) {
    setProjectOverrides(prev => {
      const next = { ...prev, [projectId]: { ...(prev[projectId] || {}), ...changes } };
      save(devsRef.current, next, customRef.current);
      return next;
    });
  }

  function addProject(projectData) {
    const id = `cp-${Date.now()}`;
    setCustomProjects(prev => {
      const next = [...prev, { id, tags: [], notes: '', phase: null, assignedDevIds: [], ...projectData }];
      save(devsRef.current, overridesRef.current, next);
      return next;
    });
  }

  const allBaseProjects = [...PROJECTS, ...customProjects];
  const projects = allBaseProjects.map(p => ({ ...p, ...(projectOverrides[p.id] || {}) }));

  if (!unlocked) return <PasswordGate onUnlock={() => setUnlocked(true)} />;

  if (loading) return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: '#060d1a',
      fontFamily: "'DM Mono', 'Fira Code', 'Consolas', monospace",
      color: '#3b82f6', fontSize: 13, letterSpacing: 3,
    }}>LOADING...</div>
  );

  return (
    <div style={{
      display: 'flex', height: '100vh',
      background: '#060d1a',
      fontFamily: "'DM Mono', 'Fira Code', 'Consolas', monospace",
      color: '#e2e8f0', overflow: 'hidden',
    }}>
      {/* ── Sidebar ── */}
      <div style={{
        width: 220, background: '#080f20',
        borderRight: '1px solid #1e293b',
        display: 'flex', flexDirection: 'column', flexShrink: 0,
      }}>
        <div style={{ padding: '24px 20px 20px', borderBottom: '1px solid #1e293b' }}>
          <div style={{ fontSize: 11, fontWeight: 900, letterSpacing: 3, color: '#3b82f6', marginBottom: 4 }}>CTO COMMAND</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: '#f1f5f9', lineHeight: 1 }}>Pipeline</div>
          <div style={{ fontSize: 9, color: '#334155', marginTop: 6, letterSpacing: 1 }}>DEV · OPS · FINANCE</div>
        </div>

        <nav style={{ flex: 1, padding: '12px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {NAV.map(n => {
            const active = tab === n.id;
            return (
              <button key={n.id} onClick={() => setTab(n.id)} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 12px', borderRadius: 8,
                border: 'none', cursor: 'pointer', textAlign: 'left',
                background: active ? '#1e3a6e' : 'transparent',
                borderLeft: active ? '3px solid #3b82f6' : '3px solid transparent',
                transition: 'all 0.15s',
              }}>
                <span style={{ fontSize: 16 }}>{n.icon}</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: active ? '#e2e8f0' : '#64748b' }}>{n.label}</div>
                  <div style={{ fontSize: 10, color: active ? '#94a3b8' : '#334155' }}>{n.desc}</div>
                </div>
              </button>
            );
          })}
        </nav>

        <div style={{ padding: '14px 16px', borderTop: '1px solid #1e293b', fontSize: 10 }}>
          <div style={{ color: '#475569', marginBottom: 2 }}>Projects tab: click dates to edit</div>
          <div style={{ color: '#3b82f6' }}>Salary tab: click salary to edit</div>
        </div>
      </div>

      {/* ── Main ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{
          height: 52, flexShrink: 0, borderBottom: '1px solid #1e293b',
          background: '#080f20', display: 'flex', alignItems: 'center',
          padding: '0 24px', gap: 16,
        }}>
          <div style={{ fontSize: 20 }}>{NAV.find(n => n.id === tab)?.icon}</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#f1f5f9' }}>{NAV.find(n => n.id === tab)?.label}</div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 6px #22c55e' }} />
            <span style={{ fontSize: 11, color: '#334155' }}>
              {projects.length} projects · {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'hidden' }}>
          {tab === 'dashboard' && <div style={{ height: '100%', overflowY: 'auto' }}><Dashboard projects={projects} developers={developers} /></div>}
          {tab === 'projects'  && <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}><ProjectsTable projects={projects} developers={developers} onUpdateProject={updateProject} onAddProject={addProject} /></div>}
          {tab === 'gantt'     && <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}><GanttChart projects={projects} developers={developers} /></div>}
          {tab === 'salary'    && <div style={{ height: '100%', overflowY: 'auto' }}><SalaryBurn projects={projects} developers={developers} onUpdateDev={updateDeveloper} onAddDev={addDeveloper} /></div>}
        </div>
      </div>
    </div>
  );
}
